import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { MapIcon, MapPinIcon } from '@heroicons/react/24/outline';
import { engineerService, EngineerJob } from '../../services/engineerService';
import { PageHeader, Badge, Button } from '../../components/ui';

function formatTime(iso?: string | null) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

const EngineerRouteView: React.FC = () => {
  const { data } = useQuery({
    queryKey: ['engineer', 'today'],
    queryFn: () => engineerService.getToday(),
  });

  const stops = useMemo<EngineerJob[]>(() => {
    if (!data?.jobs) return [];
    return [...data.jobs].sort((a, b) => {
      const at = a.scheduledDate ? Date.parse(a.scheduledDate) : Infinity;
      const bt = b.scheduledDate ? Date.parse(b.scheduledDate) : Infinity;
      return at - bt;
    });
  }, [data]);

  return (
    <div className="max-w-3xl mx-auto px-4 py-4 space-y-4">
      <PageHeader
        title="Today's route"
        subtitle="Ordered list of your stops. Tap an address to open directions in your maps app."
      />

      <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-start gap-3">
        <MapIcon className="w-5 h-5 text-slate-400 mt-0.5" />
        <div className="text-sm text-slate-600">
          A full map with auto-optimised routing is coming soon. Until then, tap any
          stop's address to open it in Google Maps.
        </div>
      </div>

      <div className="space-y-2">
        {stops.length === 0 && (
          <div className="bg-white border border-slate-200 rounded-xl p-8 text-center">
            <div className="text-sm font-semibold text-slate-700">No stops today</div>
          </div>
        )}

        {stops.map((s, i) => {
          const q = [s.address, s.postcode].filter(Boolean).join(', ');
          const mapHref = q
            ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(q)}`
            : undefined;
          return (
            <div
              key={s.id}
              className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm"
            >
              <div className="flex items-start gap-3">
                <div className="shrink-0 w-8 h-8 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold flex items-center justify-center">
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-xs text-slate-500">
                      {formatTime(s.scheduledDate) || 'Any time'}
                      {s.jobType ? ` · ${s.jobType}` : ''}
                    </div>
                    <Badge tone="info">{s.status.replace(/_/g, ' ')}</Badge>
                  </div>
                  <div className="mt-0.5 text-sm font-semibold text-slate-900 truncate">
                    {s.title}
                  </div>
                  <div className="text-xs text-slate-600 truncate">
                    {s.client?.name}
                    {s.address ? ` · ${s.address}` : ''}
                    {s.postcode ? ` · ${s.postcode}` : ''}
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <Link to={`/engineer/jobs/${s.id}`}>
                      <Button size="sm" variant="outline">
                        Open job
                      </Button>
                    </Link>
                    {mapHref && (
                      <a href={mapHref} target="_blank" rel="noreferrer">
                        <Button
                          size="sm"
                          variant="secondary"
                          leftIcon={<MapPinIcon className="w-4 h-4" />}
                        >
                          Directions
                        </Button>
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default EngineerRouteView;
